<template>
  <div id="app">
    <v-container>
      <v-snackbar v-model="message.snackbar" :timeout="message.snackbarTimeout" bottom>
        {{ message.snackbarText }}
        <v-btn dark text @click="message.snackbar = false">Close</v-btn>
      </v-snackbar>
      <v-row>
        <v-flex>
          <v-card v-if="!calibrationView && filteredAssets.length > 0">
            <v-system-bar color="orange darken-2"></v-system-bar>
            <v-app-bar dark color="orange">
              <v-tooltip bottom v-if="!getAvatarUrl(bundleAssigned)">
                <template v-slot:activator="{ on }">
                  <v-avatar size="40" class="ma-1" color="indigo" v-on="on">
                    <v-icon size="40" class="ma-1" dark>mdi-account-circle</v-icon>
                  </v-avatar>
                </template>
                <span>New user</span>
              </v-tooltip>

              <v-tooltip bottom v-else>
                <template v-slot:activator="{ on }">
                  <v-avatar size="40" v-on="on">
                    <img :src="getAvatarUrl(bundleAssigned)" />
                  </v-avatar>
                </template>
                <span>{{ bundleAssignedFullName }}</span>
              </v-tooltip>
              <v-toolbar-title class="mx-2">Your equipment</v-toolbar-title>
              <v-spacer></v-spacer>

              <v-text-field
                v-model="searchAssetText"
                placeholder="Search..."
                hide-details
                single-line
              ></v-text-field>
            </v-app-bar>

            <v-card flat v-for="item in filteredAssets" :key="item.id">
              <v-layout flat row wrap :class="`pa-3 ma-2`">
                <v-flex xs12 sm4>
                  <div class="caption grey--text">Equipment</div>
                  <div class="body-2">{{ item.category }}</div>
                </v-flex>
                <v-flex xs12 sm4>
                  <div class="caption grey--text">Model</div>
                  <div class="body-2">{{ item.model }}</div>
                </v-flex>
                <v-flex xs12 sm2>
                  <div class="caption grey--text">Serial #</div>
                  <div class="body-2">{{ item.serial }}</div>
                </v-flex>
                <v-flex xs12 sm1>
                  <div class="caption grey--text">Maintain</div>
                  <div class="body-2">{{ item.maintenance }}</div>
                </v-flex>
                <v-flex xs2 sm1 class="text-right">
                  <v-tooltip left>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        fab
                        dark
                        x-small
                        color="accent"
                        @click="getAssetServiceHistory(item.id)"
                        class="mt-1"
                      >
                        <v-icon>arrow_forward</v-icon>
                      </v-btn>
                    </template>
                    <span>View calibration history</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
              <v-divider></v-divider>
            </v-card>
          </v-card>
          <v-alert
            v-if="noEquipmentAssignedMessage"
            border="left"
            color="indigo"
            dark
          >You currently don't have any equipment assigned
          </v-alert>
        </v-flex>
      </v-row>

      <v-row>
        <v-flex v-if="calibrationView">
          <v-card class="mx-auto px-3" flat>
            <p class="pt-7 body-2 grey--text">Calibrate</p>
            <v-tooltip bottom class="mr-2 mt-8">
              <template v-slot:activator="{ on }">
                <v-btn
                  color="info"
                  dark
                  small
                  absolute
                  top
                  right
                  fab
                  v-on="on"
                  @click="calibrationView=false;"
                  class="mr-2 mt-8"
                >
                  <v-icon>keyboard_return</v-icon>
                </v-btn>
              </template>
              <span>View companies</span>
            </v-tooltip>
            <v-data-table :headers="calibrationHeader" :items="calibration" class="elevation-1">
              <template v-slot:item.type="{ item }">
                <v-chip
                  v-if="(item.result === 'Pass' && item.calibrate)"
                  color="green"
                  dark
                >Calibrated</v-chip>
                <v-chip
                  v-if="(item.result === 'Fail' && item.calibrate)"
                  color="red"
                  dark
                >Calibrated</v-chip>
                <v-chip color="success" v-if="item.service" dark>Serviced</v-chip>
                <v-chip color="warning" v-if="item.other" dark>Other</v-chip>
              </template>

              <template v-slot:item.gravitar="{ item }">
                <v-avatar size="40" v-if="getAvatarUrl(item.maintainedByEmail)" class="ml-2">
                  <img :src="getAvatarUrl(item.maintainedByEmail)" />
                </v-avatar>
                <v-icon v-else size="48" color="grey lighten-1" class="ml-1">mdi-account-circle</v-icon>
              </template>

              <template v-slot:item.date="{ item }">{{ item.date | moment("dddd, Do MMM YYYY") }}</template>

              <template v-slot:item.result="{ item }">
                <span style="color: green" v-if="item.result === 'Pass'">{{ item.result }}</span>
                <span style="color: red" v-else-if="item.result === 'Fail'">{{ item.result }}</span>
                <span v-else>{{ item.result }}</span>
              </template>
            </v-data-table>

            <v-card-text style="height: 100px; position: relative">
              <v-fab-transition>
                <v-btn
                  color="#ff0000"
                  dark
                  absolute
                  top
                  right
                  fab
                  @click="maintenanceDialog = !maintenanceDialog"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </v-fab-transition>
            </v-card-text>

            <v-dialog v-model="maintenanceDialog" max-width="500px">
              <v-card>
                <v-card-title class="grey--text">Maintenance</v-card-title>
                <v-divider></v-divider>
                <br />
                <v-card-text>
                  <v-menu
                    ref="menu"
                    :close-on-content-click="false"
                    :return-value.sync="maintenanceForm.date"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="maintenanceForm.date"
                        :rules="[rules.required]"
                        label="Calibration date"
                        prepend-icon="event"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="maintenanceForm.date" no-title scrollable>
                      <div class="flex-grow-1"></div>
                      <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
                      <v-btn text color="primary" @click="$refs.menu.save(maintenanceForm.date)">OK</v-btn>
                    </v-date-picker>
                  </v-menu>

                  <!-- <v-select
                    v-model="maintenanceForm.staff"
                    :items="staff"
                    :rules="[rules.required]"
                    label="Staff member"
                  ></v-select>-->

                  <v-radio-group v-model="maintenanceForm.type" row>
                    <v-radio label="Calibrate" value="calibrate"></v-radio>
                    <v-radio label="Service" value="service"></v-radio>
                    <v-radio label="Other" value="other"></v-radio>
                  </v-radio-group>

                  <v-textarea
                    v-if="maintenanceForm.type!=='calibrate'"
                    v-model="maintenanceForm.result"
                    filled
                    name="input-7-1"
                    label="Result"
                  ></v-textarea>

                  <v-radio-group
                    row
                    v-if="maintenanceForm.type==='calibrate'"
                    v-model="maintenanceForm.result"
                  >
                    <v-radio label="Pass" value="Pass"></v-radio>
                    <v-radio label="Fail" value="Fail"></v-radio>
                  </v-radio-group>

                  <v-textarea
                    v-if="maintenanceForm.type=='calibrate'"
                    v-model="maintenanceForm.note"
                    filled
                    name="input-7-1"
                    label="Note"
                  ></v-textarea>
                </v-card-text>

                <v-card-actions>
                  <div class="flex-grow-1"></div>
                  <v-btn text color="primary" @click="onCreateMaintenanceTask">Submit</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card>
        </v-flex>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Aws from "aws-sdk";
import { Auth } from "aws-amplify";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "./../graphql/queries";
import { onUpdateEventSlot } from "./../graphql/subscriptions";
import draggable from "vuedraggable";
import axios from "axios";
import { hpUser } from "../classes/hpUser";
export default {
  components: {
    draggable
  },
  async created() {
    await this.getUsers();
    this.getAssetsAssignedToUser();
  },
  data() {
    return {
      bundles: [],
      itemsassigned: [],
      assetTemplate: [],
      message: {
        showError: false,
        errorMessage: null,
        snackbar: false,
        snackbarText: "",
        snackbarTimeout: 3000,
        show: false,
        message: null
      },
      asset: "",
      maintenanceList: ["N/A", "Daily", "Weekly", "Monthly", "Yearly"],
      rules: {
        required: v => !!v || "This field is required.",
        minimum: v => v.length >= 8 || "Minimum 8 characters",
        currency: v =>
          /^\$?(([1-9]\d{0,4}(,\d{3})*)|0)?\.\d{1,2}$/.test(v) ||
          "GBP format is required eg 10.00",
        url: function(v) {
          if (!!v) {
            const regex = /^((ftp|http|https):\/\/).([A-z]+)\.([A-z]{2,})/;
            if (!regex.test("https://" + v)) {
              return "Url not valid";
            }
          }
          return true;
        }
      },
      hpUsers: [],
      calibrationView: false,
      noEquipmentAssignedMessage: false,
      bundleAssignedName: "",
      bundleAssigned: "",
      bundleAssignedId: "",
      bundleAssignedFullName: "",
      searchAssetText: "",
      searchBundleText: "",
      calibrationHeader: [
        {
          align: "left",
          sortable: false,
          value: "gravitar"
        },
        {
          align: "left",
          sortable: false,
          value: "type"
        },
        {
          text: "Date",
          align: "left",
          sortable: false,
          value: "date"
        },
        { text: "By", value: "maintainedBy" },
        { text: "Results", value: "result" },
        { text: "Note", value: "note" }
      ],
      calibration: [],
      maintenanceDialog: false,
      maintenanceForm: {
        date: new Date().toISOString().substr(0, 10)
      },
      user: {},
      calibrationAssetId: null
    };
  },
  methods: {
    onCreateHistory: function(history, assetId) {
      const mutation = `mutation CreateEquipmentLogHistory(
                    $date: String! 
                    $createdBy: String!
                    $createdByEmail: String!
                    $history: String!
                    $equipmentLogHistoryEquipmentLogAssetId: ID!
                ) {
                createEquipmentLogHistory (input: {
                    date: $date
                    createdBy: $createdBy
                    createdByEmail: $createdByEmail
                    history: $history
                    equipmentLogHistoryEquipmentLogAssetId: $equipmentLogHistoryEquipmentLogAssetId
                }){
                    id,
                    date
                    createdBy
                    createdByEmail
                    history
                }
            }`;

      Auth.currentAuthenticatedUser()
        .then(user => {
          let staff = this.staff.filter(
            staff => staff.value === user.attributes.email
          );

          API.graphql(
            graphqlOperation(mutation, {
              date: new Date().toISOString(),
              createdBy: staff[0].text,
              createdByEmail: staff[0].value,
              history: history,
              equipmentLogHistoryEquipmentLogAssetId: assetId
            })
          ).then(result => {
            console.log(result);
          });
        })
        .catch(err => console.log(err));
    },
    getAssetsAssignedToUser: function() {
      let that = this;
      that.itemsassigned = [];

      const query = `query ListEquipmentLogBundles($id: ID!) {
                listEquipmentLogBundles(
                    limit: 100,
                    filter: { 
                        isDeleted: { eq: 0 },
                        id: { eq: $id }
                    }
                ) {
                    items {
                        id,
                        name,
                        assigned,
                        createdAt,
                        equipmentLogAsset(
                            limit: 100,
                            filter: {
                                isDeleted: { eq: 0 }
                            }
                        ) {
                            items {
                                id,
                                product,
                                cost,
                                category,
                                hpReference,
                                model,
                                serial,
                                maintenance,
                                deliverTo
                            }
                        }
                    }
                }
            }`;

      const listEquipmentLogBundles = /* GraphQL */ `
        query ListEquipmentLogBundles(
          $filter: ModelEquipmentLogBundleFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listEquipmentLogBundles(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              name
              assigned
              createdAt
              equipmentLogAsset(limit: 100, filter: { isDeleted: { eq: 0 } }) {
                items {
                  id
                  product
                  cost
                  category
                  hpReference
                  model
                  serial
                  maintenance
                  deliverTo
                }
                nextToken
              }
            }
            nextToken
          }
        }
      `;

      Auth.currentAuthenticatedUser().then(async user => {
        const {
          data: {
            listEquipmentLogBundles: { items: bundles }
          }
        } = await API.graphql(
          graphqlOperation(listEquipmentLogBundles, {
            filter: {
              isDeleted: { eq: 0 },
              assigned: { eq: user.attributes.email }
            },
            limit: 100
          })
        );

        if(bundles.length > 0){
          let bundle = bundles[0];
          let assets = bundle.equipmentLogAsset.items;
          assets.forEach(function(asset) {
            that.itemsassigned.push({
              id: asset.id,
              serial: asset.serial,
              model: asset.model,
              cost: asset.cost,
              maintenance: asset.maintenance,
              category: asset.category,
              hpReference: asset.hpReference,
              deliverTo: asset.deliverTo
            });
          });

          that.itemsassigned.sort((a, b) => {
            return a.category
              .toLowerCase()
              .localeCompare(b.category.toLowerCase());
          });

          const assignedToPerson = this.hpUsers.filter(
            user => user.username === bundle.assigned
          );

          this.user = assignedToPerson;

          this.bundleAssignedName = bundle.name;
          this.bundleAssigned = bundle.assigned;
          this.bundleAssignedId = bundle.id;
          this.bundleAssignedFullName =
            assignedToPerson[0].firstName + " " + assignedToPerson[0].lastName;
          }
          else {
            this.noEquipmentAssignedMessage = true;
          }
      });
    },
    async getAssetServiceHistory(assetId) {
      const getEquipmentLogAsset = /* GraphQL */ `
        query GetEquipmentLogAsset($id: ID!) {
          getEquipmentLogAsset(id: $id) {
            id
            product
            category
            hpReference
            model
            serial
            purchaseDate
            cost
            password
            deliverTo
            maintenance
            supplierLink
            createdAt
            deleteReason
            isDeleted
            equipmentLogBundle {
              id
              name
              assigned
              createdAt
              isDeleted
              equipmentLogAsset {
                nextToken
              }
            }
            equipmentLogService {
              items {
                id
                date
                maintainedBy
                maintainedByEmail
                result
                note
                calibrate
                service
                other
              }
              nextToken
            }
          }
        }
      `;

      const {
        data: {
          getEquipmentLogAsset: {
            equipmentLogService: { items: maintenance }
          }
        }
      } = await API.graphql(
        graphqlOperation(getEquipmentLogAsset, { id: assetId })
      );
      this.calibration = maintenance;
      this.calibrationAssetId = assetId;
      this.calibrationView = true;
    },
    async getUsers() {
      // Get Users
      let hpUserObj = new hpUser();
      this.hpUsers = await hpUserObj.getUsers();
      this.hpUsers = _.orderBy(this.hpUsers, "firstName", "asc");
    },
    async getAssignedBundle() {
      let that = this;

      const listEquipmentLogBundles = /* GraphQL */ `
        query ListEquipmentLogBundles(
          $filter: ModelEquipmentLogBundleFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listEquipmentLogBundles(
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              name
              assigned
              createdAt
              isDeleted
              equipmentLogAsset(limit: 100, filter: { isDeleted: { eq: 0 } }) {
                items {
                  id
                }
                nextToken
              }
            }
            nextToken
          }
        }
      `;

      Auth.currentAuthenticatedUser().then(async user => {
        const {
          data: {
            listEquipmentLogBundles: { items: bundles }
          }
        } = await API.graphql(
          graphqlOperation(listEquipmentLogBundles, {
            filter: {
              isDeleted: { eq: 0 },
              assigned: { eq: user.attributes.email }
            },
            limit: 100
          })
        );

        for (var i = 0; i < bundles.length; i++) {
          const assignedToPerson = that.hpUsers.filter(
            user => user.username === bundles[i].assigned
          );

          this.bundles.push({
            id: bundles[i].id,
            name: bundles[i].name,
            assigned: bundles[i].assigned,
            assignedName: assignedToPerson
              ? `${assignedToPerson[0].firstName} ${assignedToPerson[0].lastName}`
              : bundles[i].assigned,
            createdAt: bundles[i].createdAt,
            avatar: null,
            assets: bundles[i].equipmentLogAsset.items.length
          });
        }

        const sortAlphaNum = this.bundles.sort(function(a, b) {
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base"
          });
        });
        this.bundles = sortAlphaNum;
      });

      const bundleObj = await API.graphql(
        graphqlOperation(query)
      ).then(result => {});
    },
    getAvatarUrl(username) {
      const staff = this.hpUsers.filter(user => user.username === username);
      return staff[0] ? staff[0].s3avatarUrl : null;
    },
    onCreateMaintenanceTask: function() {
      const query = `mutation CreateEquipmentLogService (
                $equipmentLogServiceEquipmentLogAssetId: ID!
                $date: String!
                $maintainedBy: String!
                $maintainedByEmail: String!
                $calibrate: Boolean
                $service: Boolean
                $other: Boolean
                $result: String
                $note: String
            ) {
                createEquipmentLogService(input: {
                    equipmentLogServiceEquipmentLogAssetId: $equipmentLogServiceEquipmentLogAssetId
                    date: $date
                    maintainedBy: $maintainedBy
                    maintainedByEmail: $maintainedByEmail
                    calibrate: $calibrate
                    service: $service
                    other: $other
                    result: $result
                    note: $note
                }){
                    date
                    maintainedBy
                    maintainedByEmail
                    result
                    calibrate
                    service
                    other
                    note
                }
            }`;

      API.graphql(
        graphqlOperation(query, {
          equipmentLogServiceEquipmentLogAssetId: this.calibrationAssetId,
          date: new Date(this.maintenanceForm.date),
          maintainedBy: this.user[0].firstName + " " + this.user[0].lastName,
          maintainedByEmail: this.user[0].username,
          calibrate: this.maintenanceForm.type === "calibrate" ? true : false,
          service: this.maintenanceForm.type === "service" ? true : false,
          other: this.maintenanceForm.type === "other" ? true : false,
          result: this.maintenanceForm.result,
          note: this.maintenanceForm.note ? this.maintenanceForm.note : null
        })
      ).then(result => {
        let task = result.data.createEquipmentLogService;
        this.calibration.push(task);
        this.message.snackbarText = "Maintenance task added";
        this.message.snackbar = true;
        this.maintenanceDialog = false;
      })
      .catch(err => {
        this.message.snackbarText = "Problem adding maintenance task";
        this.message.snackbar = true;
        this.maintenanceDialog = false;
      });
    }
  },
  computed: {
    filteredBundles() {
      if (this.searchBundleText.length > 0) {
        return this.bundles.filter(item => {
          return (
            item.name
              .toLowerCase()
              .includes(this.searchBundleText.toLowerCase()) ||
            item.assigned
              .toLowerCase()
              .includes(this.searchBundleText.toLowerCase())
          );
        });
      }
      return this.bundles;
    },
    filteredAssets() {
      if (this.searchAssetText.length > 0) {
        return this.itemsassigned.filter(item => {
          return (
            item.category
              .toLowerCase()
              .includes(this.searchAssetText.toLowerCase()) ||
            item.model
              .toLowerCase()
              .includes(this.searchAssetText.toLowerCase()) ||
            item.serial
              .toLowerCase()
              .includes(this.searchAssetText.toLowerCase()) ||
            item.maintenance
              .toLowerCase()
              .includes(this.searchAssetText.toLowerCase())
          );
        });
      }
      return this.itemsassigned;
    }
  }
};
</script>
<style>
.ghost {
  opacity: 0.5;
  background: #b9bfe4;
  border: 2px dashed orange;
}
</style>